'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export default function DashboardHeader() {
  const [lastUpdated, setLastUpdated] = useState('');
  const [counts, setCounts] = useState({ dispositivi: 0, sottoreti: 0, vlan: 0 });
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const updateTime = () => {
      setLastUpdated(new Date()?.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchCounts = async () => {
    try {
      const [{ count: dispositivi }, { count: sottoreti }, { count: vlan }] = await Promise.all([
        supabase?.from('devices')?.select('*', { count: 'exact', head: true }),
        supabase?.from('subnets')?.select('*', { count: 'exact', head: true }),
        supabase?.from('vlans')?.select('*', { count: 'exact', head: true }),
      ]);
      setCounts({
        dispositivi: dispositivi || 0,
        sottoreti: sottoreti || 0,
        vlan: vlan || 0,
      });
    } catch {
      // silent
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCounts();
    setLastUpdated(new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    setRefreshing(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const [
        { data: devices },
        { data: subnets },
        { data: vlans },
        { data: alarms },
        { data: kpi },
      ] = await Promise.all([
        supabase?.from('devices')?.select('hostname, ip_address, categoria, stato, reparto, ultima_visita').order('hostname'),
        supabase?.from('subnets')?.select('nome, cidr, gateway, area, ip_totali, ip_usati').order('nome'),
        supabase?.from('vlans')?.select('vlan_id, nome, descrizione, area').order('vlan_id'),
        supabase?.from('allarmi')?.select('titolo, priorita, stato, created_at').in('stato', ['attivo', 'riconosciuto', 'in_gestione']).order('created_at', { ascending: false }).limit(50),
        supabase?.from('kpi_snapshot')?.select('*').order('data_snapshot', { ascending: false }).limit(1),
      ]);

      const now = new Date().toLocaleString('it-IT');
      const rows: string[] = [];

      // Header info
      rows.push(`Esportazione Dashboard di Rete`);
      rows.push(`Data esportazione:,${now}`);
      rows.push(`Dispositivi totali:,${counts.dispositivi}`);
      rows.push(`Sottoreti totali:,${counts.sottoreti}`);
      rows.push(`VLAN totali:,${counts.vlan}`);
      rows.push('');

      // KPI snapshot
      if (kpi && kpi.length > 0) {
        const k = kpi[0];
        rows.push('--- KPI SNAPSHOT ---');
        rows.push('Metrica,Valore');
        if (k.uptime_medio_percentuale != null) rows.push(`Uptime %,${k.uptime_medio_percentuale}`);
        if (k.dispositivi_online != null) rows.push(`Dispositivi Online,${k.dispositivi_online}`);
        if (k.dispositivi_offline != null) rows.push(`Dispositivi Offline,${k.dispositivi_offline}`);
        if (k.allarmi_attivi != null) rows.push(`Allarmi Attivi,${k.allarmi_attivi}`);
        if (k.ticket_aperti != null) rows.push(`Ticket Aperti,${k.ticket_aperti}`);
        rows.push('');
      }

      // Devices
      if (devices && devices.length > 0) {
        rows.push('--- DISPOSITIVI ---');
        rows.push('Hostname,IP,Categoria,Stato,Reparto,Ultima Visita');
        devices.forEach((d: Record<string, unknown>) => {
          rows.push([
            d.hostname ?? '',
            d.ip_address ?? '',
            d.categoria ?? '',
            d.stato ?? '',
            d.reparto ?? '',
            d.ultima_visita ? new Date(d.ultima_visita as string).toLocaleString('it-IT') : '',
          ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
        });
        rows.push('');
      }

      // Subnets — use correct columns: cidr, gateway, area, ip_totali, ip_usati
      if (subnets && subnets.length > 0) {
        rows.push('--- SOTTORETI ---');
        rows.push('Nome,CIDR,Gateway,Area,IP Totali,IP Usati');
        subnets.forEach((s: Record<string, unknown>) => {
          rows.push([
            s.nome ?? '',
            s.cidr ?? '',
            s.gateway ?? '',
            s.area ?? '',
            s.ip_totali ?? '',
            s.ip_usati ?? '',
          ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
        });
        rows.push('');
      }

      // VLANs
      if (vlans && vlans.length > 0) {
        rows.push('--- VLAN ---');
        rows.push('VLAN ID,Nome,Descrizione,Area');
        vlans.forEach((v: Record<string, unknown>) => {
          rows.push([
            v.vlan_id ?? '',
            v.nome ?? '',
            v.descrizione ?? '',
            v.area ?? '',
          ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(','));
        });
        rows.push('');
      }

      // Active alarms
      if (alarms && alarms.length > 0) {
        rows.push('--- ALLARMI ATTIVI ---');
        rows.push('Titolo,Priorità,Stato,Data');
        alarms.forEach((a: Record<string, unknown>) => {
          rows.push([
            a.titolo ?? '',
            a.priorita ?? '',
            a.stato ?? '',
            a.created_at ? new Date(a.created_at as string).toLocaleString('it-IT') : '',
          ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
        });
      }

      const csvContent = '\uFEFF' + rows.join('\n'); // BOM for Excel UTF-8
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `network-dashboard-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // silent fail
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-3xl font-bold text-white">Pannello di Controllo Rete</h1>
        <p className="text-base text-secondary-color mt-1">
          Panoramica infrastruttura — {counts?.sottoreti} sottoreti · {counts?.vlan} VLAN · {counts?.dispositivi} dispositivi gestiti
        </p>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-surface border border-border rounded-lg px-4 py-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-green-400 pulse-online" />
          <span className="text-sm text-secondary-color font-mono">
            {lastUpdated ? `Aggiornato alle ${lastUpdated}` : 'Caricamento...'}
          </span>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 bg-surface border border-border rounded-lg px-4 py-2.5 text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all duration-150 disabled:opacity-60"
        >
          <Icon name="ArrowPathIcon" size={17} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Aggiornamento...' : 'Aggiorna'}
        </button>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-sky-500/20 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Icon name={exporting ? 'ArrowPathIcon' : 'DocumentArrowDownIcon'} size={17} className={exporting ? 'animate-spin' : ''} />
          {exporting ? 'Esportazione...' : 'Esporta'}
        </button>
      </div>
    </div>
  );
}